import Axios, { AxiosError } from "axios";
import { store } from "@src/store";
import jwt from "jsonwebtoken";
import { postRefreshToken } from "./common";
import { SIGN_IN_PATH_KEY, SIGN_UP_PATH_KEY, SOCIAL_PATH_KEY } from "@src/utils/hooks/useInitUser";
import { setResetData as setUserDataReset, setResetPersistData } from "@src/@slice/userSlice";
import { setResetData as setSignUpDataReset } from "@src/@slice/signUpSlice";

export interface IResData<T> {
  result?: number;
  data: T;
}

// axios
const axios = Axios.create();

// 토큰 재발급 상태 관리 변수
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

// 재발급 중인 요청을 대기시키고 재발급이 완료되면 대기 중인 요청을 처리
function onRrefreshed(token: string) {
  refreshSubscribers.map((callback) => callback(token));
  refreshSubscribers = [];
}

// 권한없음 상태 관리 변수 - 연속해서 권한없음 상태로 들어와 확인창 중복으로 뜨는 것 방지
let isUnauthorized = false;
let unAuthorizedTimer = null; // 특정 시간 지난 후 권한없음 상태 해제 타이머

// 요청 인터셉터
axios.interceptors.request.use(
  async function (config) {
    let accessToken = localStorage.getItem("accessToken");
    let refreshToken = localStorage.getItem("refreshToken");

    if (accessToken === 'undefined' || refreshToken === 'undefined') {
      accessToken = null;
      refreshToken = null;
    }

    if (accessToken && refreshToken) {
      // 토큰 만료 시간 체크
      // 리프레시 토큰이 엑세스 토큰보다 짧은 경우, 만료 되기 전에 재발급 요청 시도하기 위함
      // 남은 시간이 86400초보다 짧은 경우, 재발급 요청
      const accessExp = jwt.decode(accessToken)?.exp;
      const refreshExp = jwt.decode(refreshToken)?.exp;
      const now = Math.floor(Date.now() / 1000); // 현재 시간 (초 단위)
      if ((accessExp >= refreshExp || refreshExp - now < 86400) && !isRefreshing) {
        // 토큰이 재발급 중이 아닌 경우, 재발급 요청
        isRefreshing = true;
        try {
          const res = await postRefreshToken(refreshToken);
          if (res?.data?.item?.accessToken && res?.data?.item?.refreshToken) { 
            accessToken = res?.data?.item?.accessToken;
            refreshToken = res?.data?.item?.refreshToken;
            localStorage.setItem("accessToken", accessToken);
            localStorage.setItem("refreshToken", refreshToken);
          } else {
            accessToken = null;
            refreshToken = null;
          }
        } catch (error) {
          console.error("Token refresh failed", error);
        } finally {
          isRefreshing = false;
        }
      }
    }

    if (accessToken && !config?.headers["Authorization"]) {
      // 헤더에 토큰이 없는 경우에만 추가하도록 설정
      config.headers["Authorization"] = `bearer ${accessToken}`;
    }

    return config;
  },
  function (error: AxiosError) {
    return Promise.reject(error);
  },
);

// 토큰 에러 시 처리
export const axiosTokenErrorHandler = (type: "alert" | "confirm", time?: number) => {
  // 이미 권한없음 상태인 경우 바로 리턴
  if (isUnauthorized) return;

  // 도큐먼트가 hidden 상태일때는 alert/confirm 창이 뜨지 않도록 설정
  if (document.hidden) return;

  // 로그인 관련 페이지에서는 동작하지 않도록 설정
  const pathname = window.location.pathname;
  const isInSignInPath = pathname?.includes(SIGN_IN_PATH_KEY);
  const isInSignUpPath = pathname?.includes(SIGN_UP_PATH_KEY);
  const isInSocialPath = pathname?.includes(SOCIAL_PATH_KEY);
  const isInSignAdditionalPath = pathname?.includes("/sign-additional");
  if (isInSignInPath || isInSignUpPath || isInSocialPath || isInSignAdditionalPath) return;

  const params = new URLSearchParams(window.location.search);
  // loginToken 처리 중일때는 동작하지 않도록 설정
  if (params.get("loginToken")) return;

  // 권한없음 상태로 설정
  isUnauthorized = true;

  // 기존에 설정된 타이머가 있으면 제거
  if (unAuthorizedTimer) {
    clearTimeout(unAuthorizedTimer);
  }

  // time값이 있으면 time 값 이후, 없으면 5초 후 권한없음 상태 해제
  const customTime = time === undefined ? 5000 : time;
  if (customTime) {
    unAuthorizedTimer = setTimeout(() => {
      isUnauthorized = false;
    }, customTime);
  } else {
    isUnauthorized = false;
  }

  if (!type || type === "alert") {
    // token 제거 및 유저 데이터 초기화
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    store.dispatch(setUserDataReset());
    store.dispatch(setSignUpDataReset());
    store.dispatch(setResetPersistData());
    window.wb_bridge?.clearTokens?.();
    window.alert("로그인이 필요하여 로그인 페이지로 이동합니다.");
    window.location.href = "h2mwbell://login";
    return true;
  } else {
    if (window.confirm("로그인이 필요한 서비스입니다. 로그인하시겠습니까?")) {
      // token 제거 및 유저 데이터 초기화
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      store.dispatch(setUserDataReset());
      store.dispatch(setSignUpDataReset());
      store.dispatch(setResetPersistData());
      window.wb_bridge?.clearTokens?.();
      window.location.href = "h2mwbell://login";
      return true;
    } else {
      return false;
    }
  }
};

// 응답 인터셉터
axios.interceptors.response.use(
  (response) => {
    // 정상 응답을 그대로 반환
    return response;
  },
  async (error) => {
    // 응답 객체가 없거나 다른 오류인 경우 바로 거부
    if (!error.response) return Promise.reject(error);

    // 원래 요청 객체 저장
    const originalRequest = error.config;

    // 오류 코드
    const errorCode = error?.response?.data?.code;

    // 오류 타입
    let errorType = "";

    // 리프레시 토큰과 액세스 토큰을 로컬 스토리지에서 가져옴
    const refreshToken = localStorage.getItem("refreshToken");
    const accessToken = localStorage.getItem("accessToken");

    // 토근 만료 시
    if (!originalRequest._retry && errorCode === "01102") {
      // 무한 재시도 방지를 위해 재시도 플래그 설정
      originalRequest._retry = true;
      errorType = "token_expired";

      // 이미 토큰이 재발급 중인 경우 refreshSubscribers에 요청을 추가하고 대기
      if (isRefreshing) {
        return new Promise((resolve) => {
          refreshSubscribers.push((token: string) => {
            originalRequest.headers["Authorization"] = "bearer " + token;
            resolve(axios(originalRequest));
          });
        });
      }

      // 토큰이 재발급 중이 아닌 경우, 재발급 요청
      isRefreshing = true;

      // 토큰이 있는 경우
      if (refreshToken && accessToken) {
        // 토큰 갱신 요청
        try {
          const response = await postRefreshToken(refreshToken);
          // 새 토큰을 로컬 스토리지에 저장
          if (response?.data?.item?.accessToken && response?.data?.item?.refreshToken) {
            localStorage.setItem("accessToken", response.data.item.accessToken);
            localStorage.setItem("refreshToken", response.data.item.refreshToken);

            // 쿠키에 다시 저장
            // document.cookie = `h2mCookieAccessToken=${response.data.item.accessToken}; path=/; domain=.h2m.io; Secure; SameSite=None`;
            // document.cookie = `h2mCookieRefreshToken=${response.data.item.refreshToken}; path=/; domain=.h2m.io; Secure; SameSite=None`;

            // 원래 요청의 헤더에 새 액세스 토큰 설정
            originalRequest.headers["Authorization"] = "bearer " + response.data.item.accessToken;

            // 재발급 완료 후, 대기 중인 요청 처리 (토큰 갱신 시 한 번만 실행하려고)
            isRefreshing = false;
            onRrefreshed(response.data.item.accessToken);

            // 원래 요청을 다시 전송
            return axios(originalRequest);
          } else {
            console.error("Token refresh failed", error);
            // 실패 시 로그인 페이지로 이동
            axiosTokenErrorHandler("alert");
          }
        } catch (error) {
          console.error("Token refresh failed", error);
          // 실패 시 로그인 페이지로 이동
          axiosTokenErrorHandler("alert");
        }
      }
    }

    // "Unauthorized" 메시지 처리
    if (!originalRequest._retryUnauthorized && (errorCode === "01001" || errorCode === "01103")) {
      originalRequest._retryUnauthorized = true;
      errorType = "Unauthorized";

      if (errorCode === "01103" && !accessToken) {
        if (!store?.getState()?.global?.needLogin) {
          // token 없어서 발생한 에러코드 -> 로그인 페이지로 이동
          // needLogin이 true 일 때는 띄울 필요 없고, false 일 때만 띄우도록 설정
          if (!axiosTokenErrorHandler("confirm")) {
            return Promise.reject(error);
          }
        }
      } else {
        // 01001 일 때 -> 기존 userAgent 사용 방식에서 나오는 케이스?
        // 별도 처리 없이 오류를 그대로 거부
        return Promise.reject(error);
      }
    }
    // 그 외의 경우, 오류를 그대로 거부
    return Promise.reject(error);
  },
);

export default axios;

// 서버 응답 타입 (기본)
export interface ServerResponse<T = any> {
  item: T;
  result: any;
  msg?: string | null;
  total?: any;
}

export interface ServerResponseResult {
  result: {
    result: string;
    errorCode: string | null;
    errorMessage: string | null;
  };
}
